import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpContext } from '@angular/common/http'
import { catchError, map, Observable } from 'rxjs'
import { GLOBAL_LOADING_SPINNER_ENABLED } from 'src/app/shared/interceptors/loading-spinner-interceptor'
import { APIEnvelope } from 'src/app/shared/models/api'
import { EntityRef, EntityRefWithUID, ServicePointIdReference } from 'src/app/shared/models/referenceData'
import { PaginationParams, FilterParams, httpParams, withHttpParams, pagination, sort, filter } from 'src/app/shared/utilities/http-params'
import { ToastService } from '../toasts/toasts.service'
import { BaseReferenceService, ReferenceDataSource, referenceServiceTokens } from './base-reference.service'

@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  private readonly http: HttpClient = inject(HttpClient)
  private readonly toast: ToastService = inject(ToastService)
  private readonly services: Readonly<Record<ReferenceDataSource, BaseReferenceService<EntityRef>>>

  constructor() {
    const services: Partial<Record<ReferenceDataSource, BaseReferenceService<EntityRef | EntityRefWithUID>>> = {}
    const sources = Object.keys(referenceServiceTokens) as ReferenceDataSource[]
    sources.forEach(source => {
      services[source] = inject(referenceServiceTokens[source])
    })
    this.services = services as typeof this.services
  }

  init() {
    Object.values(this.services).forEach(_ => _.init())
  }

  data<T = EntityRefWithUID>(source: ReferenceDataSource, search?: string): Observable<T[]> {
    return (this.services[source]).get(search) as Observable<T[]>
  }

  servicePoints(page?: PaginationParams, filters?: FilterParams[]): Observable<ServicePointIdReference[]> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, [{ name: 'service_point_id' }]),
    )
    return this.http
      .get<APIEnvelope<ServicePointIdReference>>('service_points/', {
        params,
        context: new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, false),
      })
      .pipe(
        map(_ => _.results),
        catchError(error => {
          this.toast.show(error)
          throw error
        }),
      )
  }
}
